import React, { useState, useEffect } from 'react';
import styles from './style.module.scss';
import { Col, Row, Button, Form, Input, Select, Upload, message, Table, Card, Modal } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { api } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';

const { Option } = Select;

const baseURL = 'https://credx-bucket.s3.amazonaws.com/';

const Dashboard = () => {
  const { user } = useAuth();

  const [linhasProdutos, setLinhasProdutos] = useState([]);
  const [produtoFileList, setProdutoFileList] = useState([]);
  const [bannerFileList, setBannerFileList] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [linhas, setLinhas] = useState([]);
  const [banners, setBanners] = useState([]);
  const [isBannerModalVisible, setIsBannerModalVisible] = useState(false); // Modal para adicionar banner
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingBanner, setEditingBanner] = useState(null);

  // Fetch linhas de produtos para o dropdown e tabela
  const fetchLinhasProdutos = async () => {
    try {
      const response = await api.get('/linhas-produtos');
      setLinhasProdutos(response.data);
      setLinhas(response.data);
    } catch (error) {
      console.error(error);
      message.error('Erro ao buscar linhas de produtos');
    }
  };

  // Fetch produtos para a tabela
  const fetchProdutos = async () => {
    try {
      const response = await api.get('/produtos');
      setProdutos(response.data);
    } catch (error) {
      console.log('error', error.message);
      console.error(error);
      message.error('Erro ao buscar produtos');
    }
  };

  // Fetch banners para a tabela
  const fetchBanners = async () => {
    try {
      const response = await api.get('/banners');
      setBanners(response.data);
    } catch (error) {
      console.error(error);
      message.error('Erro ao buscar banners');
    }
  };

  useEffect(() => {
    fetchLinhasProdutos();
    fetchProdutos();
    fetchBanners();
  }, []);

  const handleUpload = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await api.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      onSuccess(response.data.data.Key);
    } catch (error) {
      console.error('Erro ao fazer upload de arquivo', error);
      onError(error);
    }
  };

  const uploadFile = (file) => {
    return new Promise((resolve, reject) => {
      handleUpload({
        file,
        onSuccess: (url) => resolve(url),
        onError: (error) => reject(error),
      });
    });
  };

  const onFinishLinhaProduto = async (values) => {
    try {
      const bannerUrl = await uploadFile(bannerFileList[0].originFileObj);
      const response = await api.post('/linhas-produtos', {
        ...values,
        banner: bannerUrl,
      });
      message.success('Linha de produto criada com sucesso');
      fetchLinhasProdutos();
    } catch (error) {
      console.error(error);
      message.error('Erro ao criar linha de produto');
    }
  };

  const onFinishProduto = async (values) => {
    try {
      const fotoUrl = await uploadFile(produtoFileList[0].originFileObj);
      const response = await api.post('/produtos', {
        ...values,
        foto: fotoUrl,
      });
      message.success('Produto criado com sucesso');
      fetchProdutos();
    } catch (error) {
      console.error(error);
      message.error('Erro ao criar produto');
    }
  };

  const onFinishBanner = async (values) => {
    try {
      const bannerUrl = await uploadFile(bannerFileList[0].originFileObj);
      await api.post('/banners', {
        ...values,
        url: bannerUrl,
        ativo: values.ativo || true, // Define o banner como ativo por padrão
      });
      message.success('Banner criado com sucesso');
      fetchBanners();
      setIsBannerModalVisible(false); // Fecha o modal após adicionar o banner
    } catch (error) {
      console.error(error);
      message.error('Erro ao criar banner');
    }
  };

  const handleFileChange =
    (setFileList) =>
    ({ fileList }) => {
      setFileList(fileList);
    };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setIsModalVisible(true);
  };

  const handleEditBanner = (banner) => {
    setEditingBanner(banner);
    setIsModalVisible(true);
  };

  const handleUpdateProduto = async (values) => {
    try {
      const fotoUrl = produtoFileList[0]?.originFileObj ? await uploadFile(produtoFileList[0].originFileObj) : editingProduct.foto;
      const response = await api.put('/produtos', {
        id: editingProduct.id,
        ...values,
        foto: fotoUrl,
      });
      message.success('Produto atualizado com sucesso');
      fetchProdutos();
      setIsModalVisible(false);
    } catch (error) {
      console.error(error);
      message.error('Erro ao atualizar produto');
    }
  };

  const handleUpdateBanner = async (values) => {
    try {
      const bannerUrl = bannerFileList[0]?.originFileObj ? await uploadFile(bannerFileList[0].originFileObj) : editingBanner.url;
      await api.put(`/banners/${editingBanner.id}`, {
        url: bannerUrl,
        ativo: values.ativo,
      });
      message.success('Banner atualizado com sucesso');
      fetchBanners();
      setIsModalVisible(false);
    } catch (error) {
      console.error(error);
      message.error('Erro ao atualizar banner');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete('/produtos', { data: { id } });
      message.success('Produto excluído com sucesso');
      fetchProdutos();
    } catch (error) {
      console.error(error);
      message.error('Erro ao excluir produto');
    }
  };

  const produtoColumns = [
    { title: 'Nome', dataIndex: 'nome', key: 'nome' },
    { title: 'Descrição', dataIndex: 'descricao', key: 'descricao' },
    { title: 'Foto', dataIndex: 'foto', key: 'foto', render: (text) => <img src={`${baseURL}${text}`} alt='foto' style={{ width: 100 }} /> },
    { title: 'Linha de Produto', dataIndex: ['linhaProduto', 'nome'], key: 'linhaProdutoId' },
    {
      title: 'Ações',
      key: 'acoes',
      render: (text, record) => (
        <span>
          <Button type='link' onClick={() => handleEdit(record)}>
            Editar
          </Button>
          <Button type='link' danger onClick={() => handleDelete(record.id)}>
            Excluir
          </Button>
        </span>
      ),
    },
  ];

  const linhaColumns = [
    { title: 'Nome', dataIndex: 'nome', key: 'nome' },
    { title: 'Descrição', dataIndex: 'descricao', key: 'descricao' },
    { title: 'Banner', dataIndex: 'banner', key: 'banner', render: (text) => <img src={`${baseURL}${text}`} alt='banner' style={{ width: 100 }} /> },
  ];

  const bannerColumns = [
    { title: 'URL', dataIndex: 'url', key: 'url', render: (text) => <img src={`${baseURL}${text}`} alt='banner' style={{ width: 100 }} /> },
    { title: 'Titulo', dataIndex: 'tituloBanner', key: 'tituloBanner', render: (text) => <p>{text}</p> },
    { title: 'Texto', dataIndex: 'textoBanner', key: 'textoBanner', render: (text) => <p>{text}</p> },
    { title: 'Ativo', dataIndex: 'ativo', key: 'ativo', render: (ativo) => (ativo ? 'Sim' : 'Não') },
    {
      title: 'Ações',
      key: 'acoes',
      render: (text, record) => (
        <span>
          <Button type='link' onClick={() => handleEditBanner(record)}>
            Editar
          </Button>
        </span>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      {/* Botão para adicionar Banner */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Button style={{ margin: 'auto', marginBottom: '2rem', width: '100%', height: '5rem' }} type='primary' onClick={() => setIsBannerModalVisible(true)}>
            Adicionar Banner
          </Button>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title='Cadastrar Linha de Produto' bordered={false}>
            <Form layout='vertical' onFinish={onFinishLinhaProduto}>
              <Form.Item name='nome' label='Nome' rules={[{ required: true, message: 'Por favor, insira o nome da linha de produto' }]}>
                <Input />
              </Form.Item>
              <Form.Item name='descricao' label='Descrição' rules={[{ required: true, message: 'Por favor, insira a descrição da linha de produto' }]}>
                <Input.TextArea />
              </Form.Item>
              <Form.Item name='banner' label='Banner' rules={[{ required: true, message: 'Por favor, insira a imagem do banner' }]}>
                <Upload customRequest={handleUpload} listType='picture-card' fileList={bannerFileList} onChange={handleFileChange(setBannerFileList)}>
                  {bannerFileList.length >= 1 ? null : (
                    <div>
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>Upload</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>
              <Form.Item>
                <Button type='primary' htmlType='submit'>
                  Cadastrar Linha de Produto
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col span={24}>
          <Card title='Cadastrar Produto' bordered={false}>
            <Form layout='vertical' onFinish={onFinishProduto}>
              <Form.Item name='nome' label='Nome' rules={[{ required: true, message: 'Por favor, insira o nome do produto' }]}>
                <Input />
              </Form.Item>
              <Form.Item name='descricao' label='Descrição' rules={[{ required: true, message: 'Por favor, insira a descrição do produto' }]}>
                <Input.TextArea />
              </Form.Item>
              <Form.Item name='linhaProdutoId' label='Linha de Produto' rules={[{ required: true, message: 'Por favor, selecione a linha de produto' }]}>
                <Select>
                  {linhasProdutos.map((linha) => (
                    <Option key={linha.id} value={linha.id}>
                      {linha.nome}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label='Fotos' rules={[{ required: true, message: 'Por favor, insira as fotos do produto' }]}>
                <Upload customRequest={handleUpload} listType='picture-card' fileList={produtoFileList} onChange={handleFileChange(setProdutoFileList)}>
                  {produtoFileList.length >= 1 ? null : (
                    <div>
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>Upload</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>
              <Form.Item>
                <Button type='primary' htmlType='submit'>
                  Cadastrar Produto
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
        <Col span={24}>
          <h2>Linhas de Produtos</h2>
          <Table dataSource={linhas} columns={linhaColumns} rowKey='id' style={{ width: '100%' }} />
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
        <Col span={24}>
          <h2>Produtos</h2>
          <Table dataSource={produtos} columns={produtoColumns} rowKey='id' style={{ width: '100%' }} />
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
        <Col span={24}>
          <h2>Banners</h2>
          <Table dataSource={banners} columns={bannerColumns} rowKey='id' style={{ width: '100%' }} />
        </Col>
      </Row>

      {/* Modal para Adicionar Banner */}
      <Modal title='Adicionar Banner' visible={isBannerModalVisible} onCancel={() => setIsBannerModalVisible(false)} footer={null}>
        <Form layout='vertical' onFinish={onFinishBanner}>
          <Form.Item name='ativo' label='Ativo'>
            <Select defaultValue={true}>
              <Option value={true}>Sim</Option>
              <Option value={false}>Não</Option>
            </Select>
          </Form.Item>
          <Form.Item label='Banner'>
            <Upload customRequest={handleUpload} listType='picture-card' fileList={bannerFileList} onChange={handleFileChange(setBannerFileList)}>
              {bannerFileList.length >= 1 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
          </Form.Item>
          <Form.Item name='tituloBanner' label='Titulo do banner' rules={[{ required: true, message: 'Por favor, insira o titulo' }]}>
            <Input />
          </Form.Item>
          <Form.Item name='textoBanner' label='Texto do banner' rules={[{ required: true, message: 'Por favor, insira o texto' }]}>
            <Input />
          </Form.Item>
          <Form.Item>
            <Button type='primary' htmlType='submit'>
              Adicionar Banner
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal para Editar Produto */}
      <Modal title='Editar Produto' visible={isModalVisible && editingProduct !== null} onCancel={() => setIsModalVisible(false)} footer={null}>
        <Form layout='vertical' onFinish={handleUpdateProduto} initialValues={editingProduct}>
          <Form.Item name='nome' label='Nome' rules={[{ required: true, message: 'Por favor, insira o nome do produto' }]}>
            <Input />
          </Form.Item>
          <Form.Item name='descricao' label='Descrição' rules={[{ required: true, message: 'Por favor, insira a descrição do produto' }]}>
            <Input.TextArea />
          </Form.Item>
          <Form.Item name='linhaProdutoId' label='Linha de Produto' rules={[{ required: true, message: 'Por favor, selecione a linha de produto' }]}>
            <Select>
              {linhasProdutos.map((linha) => (
                <Option key={linha.id} value={linha.id}>
                  {linha.nome}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label='Fotos'>
            <Upload customRequest={handleUpload} listType='picture-card' fileList={produtoFileList} onChange={handleFileChange(setProdutoFileList)}>
              {produtoFileList.length >= 1 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
          </Form.Item>
          <Form.Item>
            <Button type='primary' htmlType='submit'>
              Atualizar Produto
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal para Editar Banner */}
      <Modal title='Editar Banner' visible={isModalVisible && editingBanner !== null} onCancel={() => setIsModalVisible(false)} footer={null}>
        <Form layout='vertical' onFinish={handleUpdateBanner} initialValues={editingBanner}>
          <Form.Item name='ativo' label='Ativo'>
            <Select defaultValue={editingBanner?.ativo}>
              <Option value={true}>Sim</Option>
              <Option value={false}>Não</Option>
            </Select>
          </Form.Item>
          <Form.Item label='Banner'>
            <Upload customRequest={handleUpload} listType='picture-card' fileList={bannerFileList} onChange={handleFileChange(setBannerFileList)}>
              {bannerFileList.length >= 1 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
          </Form.Item>
          <Form.Item>
            <Button type='primary' htmlType='submit'>
              Atualizar Banner
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Dashboard;
